import { defineStore } from "pinia";
import dayjs from "dayjs";
import { getShipments } from "@/lib/api";
import { getContainerDetails } from "@/lib/parseResponseAsShipments";
import { getContainerNameForShipment } from "@/lib/utilities";
import CONSTANTS from "@/lib/CONSTANTS";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export const duplicateShipments = defineStore("duplicateShipments", {
  state: () => ({
    initialPreviousShipments: [],
    previousShipments: [],
    error: false,
    loading: false,
    allOrigins: [],
    allDestinations: [],
    reducedDestinations: [],
    reducedOrigins: []
  }),
  actions: {
    async getPreviousShipments() {
      try {
        this.loading = true;
        let shipments = [];
        const queryParams = {
          departingFromDateUtc: dayjs().utc().format("YYYY-MM-DD"),
          departingToDateUtc: dayjs().utc().add(45, "days").format("YYYY-MM-DD"),
          page: 1,
          limit: 25,
          sort: "etdUtcAtPortOfLoading:asc",
          customerPartyRoleCodes: 1
        };
        const response = await getShipments(queryParams, "shipments, parties");
        shipments = response.data;
        const total = response.headers["total-count"];
        if (total > 25) {
          queryParams.page = 2;
          const secondCall = await getShipments(queryParams, "shipments, parties");
          shipments = shipments.concat(secondCall.data);
        }
        this.initialPreviousShipments = shipments.map((shipment, index) => {
          const containers = getContainerDetails(shipment);
          const formattedShipment = {
            id: index,
            shipmentNumber: shipment.motherShipmentId,
            exportServiceModeName: shipment.exportServiceMode === "CY" ? "Container Yard" : "Store Door",
            exportServiceModeCode: shipment.exportServiceMode,
            importServiceModeName: shipment.importServiceMode === "CY" ? "Container Yard" : "Store Door",
            importServiceModeCode: shipment.importServiceMode,
            consignee: shipment.parties?.find((party) => party.maerskRoleCode == 4)?.name,
            originCityName: shipment.summarisedLocations.locations.find(
              (location) => location.lookupKey == shipment.summarisedLocations.placeOfReceiptLocationLookupKey
            ).cityName,
            originCountryCode: shipment.summarisedLocations.locations.find(
              (location) => location.lookupKey == shipment.summarisedLocations.placeOfReceiptLocationLookupKey
            ).countryCode,
            originRegionCode: shipment.summarisedLocations.locations.find(
              (location) => location.lookupKey == shipment.summarisedLocations.placeOfReceiptLocationLookupKey
            )?.regionCode,
            destinationCityName: shipment.summarisedLocations.locations.find(
              (location) => location.lookupKey == shipment.summarisedLocations.placeOfDeliveryLocationLookupKey
            ).cityName,
            destinationCountryCode: shipment.summarisedLocations.locations.find(
              (location) => location.lookupKey == shipment.summarisedLocations.placeOfDeliveryLocationLookupKey
            ).countryCode,
            destinationRegionCode: shipment.summarisedLocations.locations.find(
              (location) => location.lookupKey == shipment.summarisedLocations.placeOfDeliveryLocationLookupKey
            )?.regionCode,
            containerName: getContainerNameForShipment(
              containers.allContainersTypes,
              containers.allContainersSizes,
              containers.allContainersDisplayNames
            ),
            containerQuantity: containers.allContainers.size > 0 ? containers.allContainers.size : "-",
            containerCommodity:
              containers.allContainersCommodities.size > 0
                ? containers.allContainersCommodities.size === 1
                  ? [...containers.allContainersCommodities][0]
                  : CONSTANTS.CARGO_VARIOUS_COMMODITIES
                : "-"
          };
          formattedShipment.originDisplayName = this.cityRegionCountryDisplayName(
            formattedShipment.originCityName,
            formattedShipment.originRegionCode,
            formattedShipment.originCountryCode
          );
          formattedShipment.destinationDisplayName = this.cityRegionCountryDisplayName(
            formattedShipment.destinationCityName,
            formattedShipment.destinationRegionCode,
            formattedShipment.destinationCountryCode
          );
          return formattedShipment;
        });
        this.previousShipments = this.initialPreviousShipments;
        this.loading = false;
      } catch (error) {
        this.error = true;
        this.loading = false;
      }
    },
    updateOriginsAndDestinations() {
      let origins = [],
        destinations = [];
      origins = this.initialPreviousShipments.map((shipment) => {
        return {
          label: shipment.originDisplayName,
          value: shipment.originDisplayName
        };
      });
      destinations = this.initialPreviousShipments.map((shipment) => {
        return {
          label: shipment.destinationDisplayName,
          value: shipment.destinationDisplayName
        };
      });
      this.allOrigins = [...new Map(origins.map((item) => [item.label, item])).values()];
      this.allDestinations = [...new Map(destinations.map((item) => [item.label, item])).values()];
      this.reducedOrigins = this.allOrigins;
      this.reducedOrigins.splice(0, 0, { label: "All origins", value: "all_origins" });
      this.reducedDestinations = this.allDestinations;
      this.reducedDestinations.splice(0, 0, { label: "All destinations", value: "all_destinations" });
    },
    updateReducedOrigins(destination) {
      if (destination === "all_destinations") {
        this.reducedOrigins = this.allOrigins;
        return;
      }
      this.reducedOrigins = this.initialPreviousShipments
        .map((shipment) => {
          if (shipment.destinationDisplayName === destination) {
            return {
              label: shipment.originDisplayName,
              value: shipment.originDisplayName
            };
          }
        })
        .filter(Boolean);
      this.reducedOrigins = [...new Map(this.reducedOrigins.map((item) => [item.label, item])).values()];
      if (this.reducedOrigins.length > 1) {
        this.reducedOrigins.splice(0, 0, { label: "All origins", value: "all_origins" });
      }
    },
    updateReducedDestinations(origin) {
      if (origin === "all_origins") {
        this.reducedDestinations = this.allDestinations;
        return;
      }
      this.reducedDestinations = this.initialPreviousShipments
        .map((shipment) => {
          if (shipment.originDisplayName === origin) {
            return {
              label: shipment.destinationDisplayName,
              value: shipment.destinationDisplayName
            };
          }
        })
        .filter(Boolean);
      this.reducedDestinations = [...new Map(this.reducedDestinations.map((item) => [item.label, item])).values()];
      if (this.reducedDestinations.length > 1) {
        this.reducedDestinations.splice(0, 0, { label: "All destinations", value: "all_destinations" });
      }
    },
    updatePreviousShipments(origin, destination) {
      let reducedShipments = [];
      // show shipments with matching origin & destination
      if (origin && destination && origin !== "all_origins" && destination !== "all_destinations") {
        reducedShipments = this.initialPreviousShipments.filter((shipment) => {
          return shipment.originDisplayName === origin && shipment.destinationDisplayName === destination;
        });
      } else if (origin && origin !== "all_origins") {
        // show shipments with matching origin
        reducedShipments = this.initialPreviousShipments.filter((shipment) => {
          return shipment.originDisplayName === origin;
        });
      } else if (destination && destination !== "all_destinations") {
        // show shipments with matching destination
        reducedShipments = this.initialPreviousShipments.filter((shipment) => {
          return shipment.destinationDisplayName === destination;
        });
      } else {
        reducedShipments = this.initialPreviousShipments;
      }
      this.previousShipments = reducedShipments;
    },
    cityRegionCountryDisplayName(city, region, country) {
      return `${city}, ${region ? region + ", " : ""}${country}`;
    }
  }
});
